import Modal from "@/components/atoms/Modal";
import {
  INVALID_FORM_MESSAGE,
  INVALID_MAXIMUM_CARACTERE_MESSAGE,
  INVALID_MINIMUM_CARACTERE_MESSAGE,
  INVALID_NAME_MESSAGE,
} from "@/constants/validatorMessages";
import Course, { CourseForm } from "@/interfaces/Course";
import { IModalChildrenProps, IModalMethods } from "@/interfaces/Modal";
import { useStoreCourseMutation } from "@/services/courses";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { Col, Form, Row } from "react-bootstrap";
import { SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";
import { ToastContentProps, toast } from "react-toastify";

const StoreCourseModal: ForwardRefRenderFunction<
  IModalMethods,
  IModalChildrenProps<unknown, Course>
> = ({ onHide, callback }, ref) => {
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const defaultAdditional = useRef<{ pageIndex: number }>({
    pageIndex: 0,
  }).current;

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CourseForm>();
  const [storeCourseAsync] = useStoreCourseMutation();

  const showOrHiddenModal = () => ModalRef.current?.showOrHiddenModal();

  useImperativeHandle(ref, () => ({
    showOrHiddenModal,
  }));

  const onSubmit: SubmitHandler<CourseForm> = (data) => {
    toast.promise(storeCourseAsync(data).unwrap(), {
      pending: "Ajout de le formation en cours...",
      success: {
        render({ data }) {
          showOrHiddenModal();
          reset();
          data && callback?.(data);
          return (
            <p style={{ marginBottom: 0, textAlign: "center" }}>
              La formation à bien été ajoutée 🤩
            </p>
          );
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          if (data?.status === 422) {
            return "Les champs que vous avez remplis semblent être incorrects.";
          }

          return "Une erreur est survenue";
        },
      },
    });
  };

  const onError: SubmitErrorHandler<CourseForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <Modal
      ref={ModalRef}
      title="Ajouter une formation"
      requiredLabel
      onHide={onHide}
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <Row className="gx-3">
        <Col lg={12}>
          <Form.Group className="mb-3">
            <Form.Label>
              Nom <span className="text-danger">*</span> :
            </Form.Label>
            <Form.Control
              placeholder="ex : Formation de développement web"
              type="text"
              {...register("name", {
                required: {
                  value: true,
                  message: INVALID_NAME_MESSAGE,
                },
                minLength: {
                  value: 2,
                  message: INVALID_MINIMUM_CARACTERE_MESSAGE,
                },
                maxLength: {
                  value: 255,
                  message: INVALID_MAXIMUM_CARACTERE_MESSAGE,
                },
              })}
              isInvalid={!!errors.name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.name?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
    </Modal>
  );
};

export default forwardRef(StoreCourseModal);
